import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../utils/api';
import { formatNumber, formatMoney } from '../../utils/constants';

const COLORS = ['#2E7DE2', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#f97316', '#06b6d4', '#ec4899'];
const STATUS_LABELS = { frio: 'Frío', contactado: 'Contactado', en_conversacion: 'En conversación', llamada_agendada: 'Llamada agendada', propuesta_enviada: 'Propuesta enviada', ganado: 'Ganado', perdido: 'Perdido', no_responde: 'No responde' };
const tooltipStyle = { background: '#1c2235', border: '1px solid #252d3d', borderRadius: 8, fontFamily: 'Raleway', color: '#e8ecf4' };

export default function AdminReports() {
  const [tab, setTab] = useState('leads');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leadsData, setLeadsData] = useState(null);
  const [campaignsData, setCampaignsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/tenants').then(res => setTenants(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedTenant) params.append('tenant_id', selectedTenant);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const qs = params.toString() ? '?' + params.toString() : '';

    if (tab === 'leads') {
      api.get('/admin/reports/leads' + qs).then(res => setLeadsData(res.data)).catch(console.error).finally(() => setLoading(false));
    } else {
      if (selectedPlatform) params.append('platform', selectedPlatform);
      const qsc = params.toString() ? '?' + params.toString() : '';
      api.get('/admin/reports/campaigns' + qsc).then(res => setCampaignsData(res.data)).catch(console.error).finally(() => setLoading(false));
    }
  }, [tab, selectedTenant, selectedPlatform, startDate, endDate]);

  const totalLeads = leadsData ? leadsData.reduce((s, t) => s + t.total, 0) : 0;
  const totalGanados = leadsData ? leadsData.reduce((s, t) => s + t.ganados, 0) : 0;

  const allStatusData = leadsData ? leadsData.reduce((acc, t) => {
    (t.byStatus || []).forEach(s => {
      const existing = acc.find(a => a.status === s.status);
      if (existing) existing.count += s.count; else acc.push({ ...s });
    });
    return acc;
  }, []).map(s => ({ ...s, name: STATUS_LABELS[s.status] || s.status })) : [];

  const allSourceData = leadsData ? leadsData.reduce((acc, t) => {
    (t.bySource || []).forEach(s => {
      const existing = acc.find(a => a.source === s.source);
      if (existing) existing.count += s.count; else acc.push({ ...s });
    });
    return acc;
  }, []).map(s => ({ ...s, name: s.source })) : [];

  // Campaigns: separate by platform
  const allCampaigns = campaignsData ? campaignsData.reduce((acc, t) => {
    (t.campaigns || []).forEach(c => acc.push({ ...c, tenant_name: t.tenant_name }));
    return acc;
  }, []) : [];

  const googleCampaigns = allCampaigns.filter(c => !c.platform || c.platform === 'google_ads');
  const metaCampaigns = allCampaigns.filter(c => c.platform === 'meta_ads');
  const filteredCampaigns = selectedPlatform === 'google_ads' ? googleCampaigns : selectedPlatform === 'meta_ads' ? metaCampaigns : allCampaigns;

  const totalCost = campaignsData ? campaignsData.reduce((s, t) => s + parseFloat(t.totals?.cost || 0), 0) : 0;
  const totalClicks = campaignsData ? campaignsData.reduce((s, t) => s + parseInt(t.totals?.clicks || 0), 0) : 0;
  const totalImpressions = campaignsData ? campaignsData.reduce((s, t) => s + parseInt(t.totals?.impressions || 0), 0) : 0;
  const totalConversions = campaignsData ? campaignsData.reduce((s, t) => s + parseFloat(t.totals?.conversions || 0), 0) : 0;

  return (
    <div>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 8 }}>Reportes</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>Datos agregados de todos los clientes</p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className={tab === 'leads' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab('leads')}>Leads</button>
          <button className={tab === 'campaigns' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab('campaigns')}>Campañas</button>
        </div>
        <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)} style={{ maxWidth: 220 }}>
          <option value="">Todos los clientes</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {tab === 'campaigns' && (
          <select value={selectedPlatform} onChange={e => setSelectedPlatform(e.target.value)} style={{ maxWidth: 180 }}>
            <option value="">Todas las plataformas</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
          </select>
        )}
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ maxWidth: 160 }} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ maxWidth: 160 }} />
        {(startDate || endDate || selectedTenant || selectedPlatform) && <button className="btn btn-ghost btn-sm" onClick={() => { setStartDate(''); setEndDate(''); setSelectedTenant(''); setSelectedPlatform(''); }}>Limpiar</button>}
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', padding: 20 }}>Cargando...</div>}

      {!loading && tab === 'leads' && leadsData && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="stat-card"><div className="stat-label">Total leads</div><div className="stat-value">{formatNumber(totalLeads)}</div></div>
            <div className="stat-card"><div className="stat-label">Ganados</div><div className="stat-value" style={{ color: 'var(--success)' }}>{formatNumber(totalGanados)}</div></div>
            <div className="stat-card"><div className="stat-label">Tasa de conversión</div><div className="stat-value">{totalLeads > 0 ? ((totalGanados / totalLeads) * 100).toFixed(1) : 0}%</div></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Por estado</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={allStatusData}><CartesianGrid strokeDasharray="3 3" stroke="#252d3d" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis /><Tooltip contentStyle={tooltipStyle} /><Bar dataKey="count" fill="#2E7DE2" radius={[4,4,0,0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Por fuente</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart><Pie data={allSourceData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => name + ' ' + (percent * 100).toFixed(0) + '%'}>
                  {allSourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {!selectedTenant && leadsData.length > 1 && (
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Por cliente</h3>
              <div className="table-container">
                <table><thead><tr><th>Cliente</th><th>Total</th><th>Ganados</th><th>Conversión</th><th>Web</th></tr></thead>
                  <tbody>
                    {leadsData.map(t => (
                      <tr key={t.tenant_id}>
                        <td style={{ fontWeight: 600 }}>{t.tenant_name}</td>
                        <td>{formatNumber(t.total)}</td>
                        <td style={{ color: 'var(--success)' }}>{formatNumber(t.ganados)}</td>
                        <td>{t.conversion_rate}%</td>
                        <td>{formatNumber(t.web_leads)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'campaigns' && campaignsData && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="stat-card"><div className="stat-label">Inversión total</div><div className="stat-value">{formatMoney(totalCost)}</div></div>
            <div className="stat-card"><div className="stat-label">Clicks</div><div className="stat-value">{formatNumber(totalClicks)}</div></div>
            <div className="stat-card"><div className="stat-label">Impresiones</div><div className="stat-value">{formatNumber(totalImpressions)}</div></div>
            <div className="stat-card"><div className="stat-label">Conversiones</div><div className="stat-value">{formatNumber(totalConversions)}</div></div>
          </div>

          {/* Platform summary */}
          {!selectedPlatform && googleCampaigns.length > 0 && metaCampaigns.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div className="card" style={{ borderLeft: '3px solid #4285F4' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 16 }}>G</span> Google Ads</div>
                <div style={{ display: 'flex', gap: 24 }}>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Campañas</div><div style={{ fontWeight: 700 }}>{googleCampaigns.length}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Inversión</div><div style={{ fontWeight: 700 }}>{formatMoney(googleCampaigns.reduce((s,c) => s + (parseFloat(c.total_cost)||0), 0))}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Clicks</div><div style={{ fontWeight: 700 }}>{formatNumber(googleCampaigns.reduce((s,c) => s + (parseInt(c.total_clicks)||0), 0))}</div></div>
                </div>
              </div>
              <div className="card" style={{ borderLeft: '3px solid #1877F2' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 16 }}>M</span> Meta Ads</div>
                <div style={{ display: 'flex', gap: 24 }}>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Campañas</div><div style={{ fontWeight: 700 }}>{metaCampaigns.length}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Inversión</div><div style={{ fontWeight: 700 }}>{formatMoney(metaCampaigns.reduce((s,c) => s + (parseFloat(c.total_cost)||0), 0))}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Clicks</div><div style={{ fontWeight: 700 }}>{formatNumber(metaCampaigns.reduce((s,c) => s + (parseInt(c.total_clicks)||0), 0))}</div></div>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Campañas {selectedPlatform === 'google_ads' ? '(Google Ads)' : selectedPlatform === 'meta_ads' ? '(Meta Ads)' : '(Todas)'}</h3>
            <div className="table-container">
              <table><thead><tr><th>Cliente</th><th>Plataforma</th><th>Campaña</th><th>Inversión</th><th>Clicks</th><th>CTR</th><th>CPC</th><th>Leads CRM</th><th>CPL Real</th></tr></thead>
                <tbody>
                  {filteredCampaigns.map((c, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.tenant_name}</td>
                      <td><span className="badge" style={{ fontSize: 10, background: (c.platform === 'meta_ads') ? 'rgba(24,119,242,0.15)' : 'rgba(66,133,244,0.15)', color: (c.platform === 'meta_ads') ? '#1877F2' : '#4285F4' }}>{(c.platform === 'meta_ads') ? 'Meta' : 'Google'}</span></td>
                      <td style={{ fontWeight: 600 }}>{c.campaign_name}</td>
                      <td>{formatMoney(c.total_cost)}</td>
                      <td>{formatNumber(c.total_clicks)}</td>
                      <td>{(parseFloat(c.avg_ctr) || 0).toFixed(2)}%</td>
                      <td>{formatMoney(c.avg_cpc)}</td>
                      <td style={{ color: '#2E7DE2', fontWeight: 600 }}>{c.crm_leads || 0}</td>
                      <td style={{ color: c.cost_per_lead ? '#eab308' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_lead ? formatMoney(c.cost_per_lead) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
